import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Modal, Button, Form } from 'react-bootstrap';
import Select from 'react-select';
import DatePicker from "react-datepicker";
import { createMaterialImport } from '../../actions/materialImports';
import { getWarehouseOptions, getMaterialOptions, clearMaterialOptions, getSupplierOptions } from '../../actions/material';
import { getFirmOptionsForManagers } from '../../actions/user';
import { usePermission } from '../../hooks/usePermission';
import MaterialImportItemForm from './MaterialImportItemForm';
import { Plus } from 'react-feather';
import { formatDateForSubmission } from '../../utils/dateHelper';

const MaterialImportCreateForm = ({
    show,
    handleClose,
    createMaterialImport,
    getSupplierOptions,
    getWarehouseOptions,
    getMaterialOptions,
    clearMaterialOptions,
    getFirmOptionsForManagers,
    materials = [],
    suppliers = [],
    warehouses = [],
    firms = []
}) => {
    const { userCan } = usePermission();

    if (!userCan('create material imports')) {
        return null;
    }

    const [formData, setFormData] = useState({
        supplier_id: '',
        firm_id: '',
        warehouse_id: '',
        date: new Date(),
        is_for_special_sale: false,
        description: '',
        items: [{ material_id: '', quantity: '', price: '' }]
    });

    const [selectedSupplier, setSelectedSupplier] = useState(null);
    const [selectedFirm, setSelectedFirm] = useState(null);
    const [selectedWarehouse, setSelectedWarehouse] = useState(null);

    useEffect(() => {
        getSupplierOptions();
        getFirmOptionsForManagers();
        // Initially load all material warehouses
        getWarehouseOptions({ type: 'material' });
        // Clear materials initially so nothing shows until firm and warehouse are selected
        clearMaterialOptions();
    }, [getSupplierOptions, getFirmOptionsForManagers, getWarehouseOptions, clearMaterialOptions]);


    // Filter materials when firm or warehouse changes
    useEffect(() => {
        // Only load materials when both firm and warehouse are selected
        if (selectedFirm?.value && selectedWarehouse?.value) {
            getMaterialOptions({
                firm_id: selectedFirm.value,
                warehouse_id: selectedWarehouse.value
            });
        } else {
            // Clear materials if either firm or warehouse is not selected
            clearMaterialOptions();
        }
    }, [selectedFirm, selectedWarehouse, getMaterialOptions, clearMaterialOptions]);

    const { date, is_for_special_sale, description, items } = formData;

    const onChange = e => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const onItemChange = (index, field, value) => {
        const newItems = [...items];
        // Convert quantity and price to numbers with 2 decimal places
        if (field === 'quantity' || field === 'price') {
            value = value === '' ? '' : Number(parseFloat(value).toFixed(2));
        }
        newItems[index] = { ...newItems[index], [field]: value };
        setFormData({ ...formData, items: newItems });
    };

    const addItem = () => {
        setFormData({
            ...formData,
            items: [...items, { material_id: '', quantity: '', price: '' }]
        });
    };

    const removeItem = (index) => {
        if (items.length > 1) {
            const newItems = items.filter((_, i) => i !== index);
            setFormData({ ...formData, items: newItems });
        }
    };



    const onSubmit = e => {
        e.preventDefault();
        // Convert all quantities and prices to numbers with 2 decimal places
        const processedItems = items.map(item => ({
            ...item,
            quantity: Number(parseFloat(item.quantity || 0).toFixed(2)),
            price: is_for_special_sale ? 0 : Number(parseFloat(item.price || 0).toFixed(2))
        }));
        createMaterialImport({
            ...formData,
            items: processedItems,
            supplier_id: selectedSupplier?.value,
            firm_id: selectedFirm?.value,
            warehouse_id: selectedWarehouse?.value,
            date: formatDateForSubmission(date)
        });
        handleClose();
        setFormData({
            supplier_id: '',
            firm_id: '',
            warehouse_id: '',
            date: new Date(),
            is_for_special_sale: false,
            description: '',
            items: [{ material_id: '', quantity: '', price: '' }]
        });
        setSelectedSupplier(null);
        setSelectedFirm(null);
        setSelectedWarehouse(null);
    };

    const materialOptions = materials?.map(material => ({
        value: material.id,
        label: material.name,
        unit: material.unit,
        unit_alternative: material.unit_alternative
    })) || [];

    const supplierOptions = suppliers?.map(supplier => ({
        value: supplier.id,
        label: supplier.name
    })) || [];

    const firmOptions = firms?.map(firm => ({
        value: firm.id,
        label: firm.name
    })) || [];

    // Don't filter warehouses on frontend, just map what we get from the API
    const warehouseOptions = warehouses.map(warehouse => ({
        value: warehouse.id,
        label: warehouse.name
    }));

    const handleFirmChange = (selected) => {
        setSelectedFirm(selected);
        setSelectedWarehouse(null); // Reset warehouse when firm changes
        
        // Reset material selections in items when firm changes
        const resetItems = items.map(item => ({
            ...item,
            material_id: ''
        }));
        setFormData({
            ...formData,
            items: resetItems
        });
        
        // Refetch warehouses with the selected firm_id
        if (selected && selected.value) {
            getWarehouseOptions({ firm_id: selected.value, type: 'material' });
        } else {
            // If no firm is selected, get all material warehouses
            getWarehouseOptions({ type: 'material' });
        }
    };

    const handleWarehouseChange = (selected) => {
        setSelectedWarehouse(selected);
        
        // Reset material selections in items when warehouse changes
        const resetItems = items.map(item => ({
            ...item,
            material_id: ''
        }));
        setFormData({
            ...formData,
            items: resetItems
        });
    };

    return (
        <Modal show={show} onHide={handleClose} size="xl">
            <Modal.Header closeButton>
                <Modal.Title>Material Import qo'shish</Modal.Title>
            </Modal.Header>
            <Form onSubmit={onSubmit}>
                <Modal.Body>
                    <div className="row">
                        <div className="col-md-3">
                            <Form.Group className="mb-3">
                                <Form.Label>Ta'minotchi</Form.Label>
                                <Select
                                    value={selectedSupplier}
                                    onChange={setSelectedSupplier}
                                    options={supplierOptions}
                                    placeholder="Ta'minotchini tanlang..."
                                    isSearchable={true}
                                />
                            </Form.Group>
                        </div>
                        <div className="col-md-3">
                            <Form.Group className="mb-3">
                                <Form.Label>Firma</Form.Label>
                                <Select
                                    value={selectedFirm}
                                    onChange={handleFirmChange}
                                    options={firmOptions}
                                    placeholder="Firmani tanlang..."
                                    isSearchable={true}
                                />
                            </Form.Group>
                        </div>
                        <div className="col-md-3">
                            <Form.Group className="mb-3">
                                <Form.Label>Ombor</Form.Label>
                                <Select
                                    value={selectedWarehouse}
                                    onChange={handleWarehouseChange}
                                    options={warehouseOptions}
                                    placeholder="Omborni tanlang..."
                                    isSearchable={true}
                                    isDisabled={!selectedFirm}
                                />
                                {selectedFirm && warehouseOptions.length === 0 && (
                                    <small className="text-danger">
                                        Bu firma uchun material tipdagi omborlar mavjud emas
                                    </small>
                                )}
                            </Form.Group>
                        </div>
                        <div className="col-md-3">
                            <Form.Group className="mb-3">
                                <Form.Label>Sana</Form.Label>
                                <div className="d-flex align-items-center gap-2">
                                    <DatePicker
                                        selected={date}
                                        onChange={date => setFormData({ ...formData, date })}
                                        className="form-control"
                                        wrapperClassName='w-100'
                                        dateFormat="yyyy-MM-dd"
                                    />
                                </div>
                            </Form.Group>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-md-12">
                            <Form.Group className="mb-3">
                                <Form.Label>Tavsif</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="description"
                                    value={description}
                                    onChange={onChange}
                                    placeholder="Tavsifni kiriting..."
                                />
                            </Form.Group>
                        </div>
                    </div>

                    <div className="row mb-3">
                        <div className="col-md-12">
                            <Form.Check
                                type="checkbox"
                                label="Maxsus sotuvga mo'ljallangan"
                                name="is_for_special_sale"
                                checked={is_for_special_sale}
                                onChange={e => setFormData({ ...formData, is_for_special_sale: e.target.checked })}
                            />
                        </div>
                    </div>

                    <div className="border rounded p-3 mb-3">
                        <h5 className="mb-3">Materiallar</h5>
                        {items.map((item, index) => (
                            <MaterialImportItemForm
                                key={index}
                                item={item}
                                index={index}
                                materialOptions={materialOptions}
                                onChange={onItemChange}
                                onRemove={removeItem}
                                isForSpecialSale={is_for_special_sale}
                            />
                        ))}
                        <div className="d-flex justify-content-center">
                            <Button 
                                variant="outline-primary" 
                                onClick={addItem}
                                className="d-flex align-items-center"
                            >
                                <Plus size={16} className="me-2" />
                                Material qo'shish
                            </Button>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        Bekor qilish
                    </Button>
                    <Button variant="primary" type="submit" disabled={!selectedSupplier || !selectedFirm || !selectedWarehouse}>
                        Saqlash
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

MaterialImportCreateForm.propTypes = {
    show: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    createMaterialImport: PropTypes.func.isRequired,
    getSupplierOptions: PropTypes.func.isRequired,
    getWarehouseOptions: PropTypes.func.isRequired,
    getMaterialOptions: PropTypes.func.isRequired,
    clearMaterialOptions: PropTypes.func.isRequired,
    getFirmOptionsForManagers: PropTypes.func.isRequired,
    materials: PropTypes.array,
    suppliers: PropTypes.array,
    warehouses: PropTypes.array,
    firms: PropTypes.array
};

const mapStateToProps = state => ({
    materials: state.material.material_options,
    suppliers: state.material.supplier_options,
    warehouses: state.material.material_warehouses_options,
    firms: state.user.firm_options
});

export default connect(mapStateToProps, { 
    createMaterialImport, 
    getSupplierOptions,
    getWarehouseOptions,
    getMaterialOptions,
    clearMaterialOptions,
    getFirmOptionsForManagers
})(MaterialImportCreateForm);
